import React, { useState, useEffect } from 'react';
import { SavedRetirementScenario, UserProfile } from '../types/retirementComparison';
import { ComparisonScenariosProps } from '../types/shared';
import retirementScenariosService from '../services/retirement-scenarios';

interface RetirementSavedScenariosProps extends ComparisonScenariosProps {
  currentData?: UserProfile;
}

const RetirementSavedScenarios: React.FC<RetirementSavedScenariosProps> = ({
  comparisonMode = false, 
  user, 
  guestScenario, 
  currentData,
  onLoadScenarioToA, 
  onLoadScenarioToB, 
  onSaveScenario 
}) => {
  const [savedScenarios, setSavedScenarios] = useState<SavedRetirementScenario[]>([]);
  const [mode, setMode] = useState<'new' | 'saved'>('new');
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [saveName, setSaveName] = useState('');
  const [saveNameError, setSaveNameError] = useState<boolean>(false);

  // Load saved scenarios from backend
  const loadSavedScenarios = async () => {
    if (!user?.email) {
      setSavedScenarios([]);
      return;
    }
    try {
      const scenariosArray = await retirementScenariosService.getRetirementScenariosArray();
      setSavedScenarios(scenariosArray);
    } catch (error) {
      console.error('Error loading retirement scenarios:', error);
      setSavedScenarios([]);
    }
  };

  useEffect(() => {
    loadSavedScenarios();
  }, [user?.email]);

  const handleSave = async () => {
    const name = saveName.trim();

    // Require a name to be provided
    if (!name) {
      setSaveNameError(true);
      return;
    }

    // Clear any previous error state
    setSaveNameError(false);

    const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === name.toLowerCase());

    try {
      if (existingScenario) {
        // Update existing scenario
        await retirementScenariosService.updateRetirementScenario(existingScenario.id, {
          name: name,
          data: currentData || {} as UserProfile,
          description: `Updated on ${new Date().toLocaleDateString()}`
        });
      } else {
        // Create new scenario
        await retirementScenariosService.saveRetirementScenario({
          name: name,
          data: currentData || {} as UserProfile,
          description: `Saved on ${new Date().toLocaleDateString()}`
        });
      }

      // Reload scenarios from backend
      await loadSavedScenarios();

      // Notify parent component
      if (onSaveScenario) {
        onSaveScenario({ name, data: currentData });
      }
    } catch (error) {
      console.error('Error saving retirement scenario:', error);
    }
  };

  const handleLoad = () => {
    if (!selectedScenario) return;
    const scenario = savedScenarios.find(s => s.id === selectedScenario);
    if (scenario && onLoadScenarioToA) {
      onLoadScenarioToA(scenario.data);
    }
  };

  const handleDelete = async () => {
    if (!selectedScenario) return;
    try {
      await retirementScenariosService.deleteRetirementScenario(selectedScenario);
      await loadSavedScenarios();
      setSelectedScenario('');
    } catch (error) {
      console.error('Error deleting retirement scenario:', error);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const selectedScenarioData = savedScenarios.find(s => s.id === selectedScenario);

  return (
    <div className="scenarios-section" style={{
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '16px',
      margin: '16px 0 24px'
    }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', fontWeight: '600', color: '#374151' }}>
        Scenarios
      </h3>
      
      {/* Scenario Name Input and Save Button */}
      {user ? (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Scenario Name *"
            value={saveName}
            onChange={(e) => {
              setSaveName(e.target.value);
              if (saveNameError) {
                setSaveNameError(false);
              }
            }}
            className={saveNameError ? 'scenario-input-error' : ''}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: saveNameError ? '1px solid #dc2626' : '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif'
            }}
          />
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              fontSize: '0.9rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Save
          </button>
        </div>
      ) : (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '16px',
          padding: '12px',
          background: '#f3f4f6',
          borderRadius: '6px',
          border: '1px solid #d1d5db'
        }}>
          <span style={{ 
            flex: 1, 
            fontSize: '0.9rem', 
            color: '#6b7280',
            fontStyle: 'italic',
            padding: '12px'
          }}>
            💾 Create an account to save scenarios
          </span>
          <button
            onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
            style={{
              padding: '8px 16px',
              fontSize: '0.9rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Create Account
          </button>
        </div>
      )}

      {/* Saved Scenarios List */}
      {savedScenarios.length > 0 && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {savedScenarios.map(scenario => (
            <div key={scenario.id} style={{ 
              background: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '12px 16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                    {scenario.name}
                  </h4>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {formatTimestamp(scenario.createdAt)}
                  </div>
                  {scenario.results && (
                    <div style={{ fontSize: '0.8rem', color: '#059669', marginTop: '4px' }}>
                      ✓ Analysis Complete
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                  {comparisonMode ? (
                    <>
                      <button
                        onClick={() => onLoadScenarioToA && onLoadScenarioToA(scenario.data, scenario.name)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.85rem',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Load to Scenario A
                      </button>
                      {onLoadScenarioToB && (
                        <button
                          onClick={() => onLoadScenarioToB(scenario.data, scenario.name)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '0.85rem',
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            border: '1px solid #93c5fd',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Load to Scenario B
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => onLoadScenarioToA && onLoadScenarioToA(scenario.data, scenario.name)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Load
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to delete "${scenario.name}"?`)) {
                        try {
                          await retirementScenariosService.deleteRetirementScenario(scenario.id);
                          await loadSavedScenarios();
                        } catch (error) {
                          console.error('Error deleting retirement scenario:', error);
                        }
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No scenarios message */}
      {savedScenarios.length === 0 && user && (
        <div style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: '0.9rem',
          padding: '20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          No saved scenarios yet. Configure your retirement analysis and click Save.
        </div>
      )}

      {/* Guest user message */}
      {!user && (
        <div style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: '0.9rem',
          padding: '20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          Create an account to save and manage multiple retirement scenarios.
        </div>
      )}
    </div>
  );
};

export default RetirementSavedScenarios;